import { useMemo } from "react";
import log from "logbeacon";

import { TaskListResponse } from "@/src/features/api/QuestsApi";
import { usePostTaskList } from "@hooks/usePostTaskList";
import { useIsPc } from "./useIsPc";

type UseTaskCenterTasksParams = {
  locale?: string;
  channel?: string;
  position?: string;
};

const TASK_REWARD_TYPES = ["CREDIT", "CREDIT_POINT"] as const;
const TASK_AREAS_WITH_SUB_TASK = [
  "Onboarding Missions",
  "Daily Missions",
] as const;

const expandTasks = (allTasks: TaskListResponse[] = []): TaskListResponse[] => {
  return allTasks
    .filter((task) => !!task.area)
    .flatMap(
      (
        task,
      ): Pick<
        TaskListResponse,
        "status" | "rewardStatus" | "code" | "rewards"
      >[] => {
        if (
          !task.area ||
          !TASK_AREAS_WITH_SUB_TASK.includes(
            task.area as (typeof TASK_AREAS_WITH_SUB_TASK)[number],
          )
        ) {
          return [task];
        }

        if (Array.isArray(task.subTask) && task.subTask.length > 0) {
          return [task, ...task.subTask];
        }

        return [task];
      },
    );
};

const hasClaimableReward = (
  task: Pick<TaskListResponse, "rewards">,
): boolean => {
  return !!task.rewards?.some((reward) =>
    TASK_REWARD_TYPES.includes(
      (reward.type ?? "") as (typeof TASK_REWARD_TYPES)[number],
    ),
  );
};

export const useTaskCenterTasks = ({
  locale,
  channel,
  position = "list",
}: UseTaskCenterTasksParams) => {
  const [isPc] = useIsPc();
  const { postTaskList: allTasks, ...rest } = usePostTaskList({
    position,
    channel,
    locale,
  });

  const taskStats = useMemo(() => {
    if (!allTasks?.length) {
      return {
        allClaimedTasks: [] as TaskListResponse[],
        unclaimedTasks: [] as TaskListResponse[],
        doableTasks: [] as TaskListResponse[],
        hasDoableTask: false,
        unclaimedCount: 0,
      };
    }

    const allClaimedTasks = expandTasks(allTasks);

    const unclaimedTasks = allClaimedTasks.filter(
      (task) =>
        task.status === 1 &&
        !task.rewardStatus &&
        hasClaimableReward(task) &&
        (!isPc || task.code !== "login_task"),
    );

    const doableTasks = allClaimedTasks.filter((task) => task.status !== 1);

    const registrationNewTask = allClaimedTasks.find(
      (task) => task.code === "registration_new",
    );

    const registrationPendingCount =
      registrationNewTask && !registrationNewTask.status ? 1 : 0;

    log.debug(
      "[useTaskCenterTasks] allClaimedTasks",
      allClaimedTasks,
      unclaimedTasks,
      doableTasks,
      registrationNewTask,
    );

    return {
      allClaimedTasks,
      unclaimedTasks,
      doableTasks,
      hasDoableTask: doableTasks.length > 0,
      unclaimedCount: unclaimedTasks.length + registrationPendingCount,
    };
  }, [allTasks, isPc]);

  return {
    ...taskStats,
    allTasks,
    ...rest,
  };
};
